import { DateTime, Interval } from 'luxon';
import { useMemo } from 'react';
import { indexedPortfolioTimeValueDataPoints, timeValueDataPointsWithinInterval } from '../../utils/functions';
import { useGetModelPortfoliosMonthlyReturns } from './getModelPortfolioMonthlyReturns';
import { getAnnualReturn } from '../../utils/functions';
import { TimeValueDataPoint } from '../../components/funds-robot/comparison-screen/comparison-chart/ValueGrowthChart';

export const useGetYearlyReturnsModelPortfolio = (
    startDate: DateTime | Date,
    id?: string,
    givenInterval?: Interval,
) => {
    const fundRatesQuery = useGetModelPortfoliosMonthlyReturns(id);

    const getIndexedInInterval = (timeValueDataPoints: TimeValueDataPoint[], interval?: Interval) => {
        const withinInterval = interval
            ? timeValueDataPointsWithinInterval(timeValueDataPoints, interval)
            : timeValueDataPoints;
        return indexedPortfolioTimeValueDataPoints(withinInterval);
    };

    const timeValueDataPoints = useMemo(() => {
        return (
            fundRatesQuery.data?.monthlyReturnRates
                .map((r) => {
                    return {
                        t: DateTime.fromISO(r.date).toMillis(),
                        y: r.return,
                    };
                })
                .sort((t1, t2) => (t1.t < t2.t ? 1 : t2.t < t1.t ? -1 : 0)) || [] // DESC sorting
        );
    }, [fundRatesQuery.data]);

    const intervalThisYear = Interval.fromDateTimes(DateTime.now().startOf('year'), DateTime.now());
    const intervalSinceStart =
        timeValueDataPoints[0] && Interval.fromDateTimes(startDate, DateTime.fromMillis(timeValueDataPoints[0].t));

    const indexedThisYear = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, intervalThisYear),
        [timeValueDataPoints, intervalThisYear],
    );
    const indexedSinceStart = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, intervalSinceStart),
        [timeValueDataPoints, intervalSinceStart],
    );
    const indexedGivenInterval = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, givenInterval),
        [timeValueDataPoints, givenInterval],
    );

    // Only show annual returns when the fund existed longer than a year
    const indexedAnnual =
        intervalSinceStart?.length('years') >= 1
            ? givenInterval && indexedGivenInterval[0]
                ? getAnnualReturn(indexedGivenInterval[0].y - 100, givenInterval)
                : indexedSinceStart[0] && getAnnualReturn(indexedSinceStart[0].y - 100, intervalSinceStart)
            : undefined;

    return {
        thisYear: indexedThisYear[0] ? indexedThisYear[0].y - 100 : undefined,
        annual: indexedAnnual ? indexedAnnual : undefined,
        total: indexedSinceStart[0] ? indexedSinceStart[0].y - 100 : undefined,
    };
};
